/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import StructureContainers from '@Services/StructureContainers';
import RxThumbnail from '@Services/RxThumbnail';
import { StructureOptionsBadges } from '@Containers/StructureOptions';
import { useDispatch } from 'react-redux';
import { ButtonWithIcon } from '@els/els-react--button';
import MarvinHelpers from '@Services/MarvinHelpers';
import get from 'lodash/get';
import { updateStructureandAdvancedAction } from '@Pages/QueryBuilder/QueryletActions';
import history from '@AppRoutes/history';
import { useLocation } from 'react-router-dom';

function Structure({ structureType }) {
  const [structure, setStructure] = useState(structureType);
  const dispatch = useDispatch();
  const location = useLocation();
  const importedQueryValue = location.state && location.state.importedQuery;

  function getStructurePreviewfromData() {
    RxThumbnail.gitStructureQueryLetThumbnail(structure.structureData).then((data) => {
      setStructure((_structure) => ({
        ..._structure,
        structurePreview: data.url,
        previewSize: MarvinHelpers.getParams(MarvinHelpers.getBoundCount(structure.structureData, 4)),
      }));
    });
  }

  function setStructureFn(structureContainer) {
    setStructure((_structure) => {
      const updatedStructure = {
        ..._structure,
        ...(structureContainer.data && {
          structureData: structureContainer.data,
          previewSize: MarvinHelpers.getParams(MarvinHelpers.getBoundCount(structureContainer.data, 4)),
        }),
        ...(structureContainer.preview && { structurePreview: structureContainer.preview }),
        ...(structureContainer.name && { structureName: structureContainer.name }),
        ...(structureContainer.options && { options: structureContainer.options }),
      };
      if (!structureType.structureData) {
        dispatch(updateStructureandAdvancedAction(updatedStructure));
      }

      return updatedStructure;
    });
    if (!structureContainer.preview) {
      getStructurePreviewfromData();
    }
  }

  useEffect(() => {
    StructureContainers.getContainerByUID(structure.structureUID)

      .then((structureContainer) => {
        if (isEmpty(structureContainer)) {
          if (structure.structureData) {
            getStructurePreviewfromData();
          }
        } else {
          setStructureFn(structureContainer);
        }
      })
      .catch(Logger.reject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onStructureBuilderClick() {
    StructureContainers.saveContainer(structure.structureUID, {
      data: structure.structureData,
      options: structure.options,
      preview: structure.structurePreview,
      name: structure.structureName,
    })
      .then(() =>
        history.push({
          pathname: `/structure-editor/${structure.structureUID}`,
          state: { target: 'advancedSearch', structureUID: structure.structureUID },
        })
      )
      .catch(Logger.error);
  }

  function clearStructure($event) {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }

    StructureContainers.removeContainerByUID(structure.structureUID);
    setStructure((prevStructure) => {
      const updatedStructure = {
        ...prevStructure,
        structurePreview: null,
        structureData: null,
        options: null,
        structureName: null,
      };

      dispatch(updateStructureandAdvancedAction(updatedStructure));

      return updatedStructure;
    });
  }

  const updateOptions = () =>
    StructureContainers.getContainerByUID(structure.structureUID)
      .then((structureContainer) => {
        const structureContainerOptions = get(structureContainer, 'options');
        setStructure((_structure) => {
          const updatedStructure = {
            ..._structure,
            ...(structureContainerOptions && {
              options: structureContainerOptions,
            }),
          };

          dispatch(updateStructureandAdvancedAction(updatedStructure));

          return updatedStructure;
        });
      })
      .catch(Logger.reject);

  return (
    <div className="rx-search--structure-drawing">
      {(!structure.exists || !structure.canExist) &&
        (!structure.structurePreview ? (
          <ButtonWithIcon
            id="structure-draw"
            type="link"
            sprite="DrawStructureNew"
            a11y={{ name: 'create structure', description: 'create structure button' }}
            onClick={() => onStructureBuilderClick()}>
            Create Structure / Reaction Drawing
          </ButtonWithIcon>
        ) : (
          <>
            <div
              className="search-structure-preview"
              onClick={() => {
                onStructureBuilderClick();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStructureBuilderClick();
                }
              }}>
              <ButtonWithIcon
                sprite="Close"
                linkIconSize="xs"
                iconOnly
                id="querybuiler-clearStructure-button"
                type="link"
                onClick={(e) => clearStructure(e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    clearStructure(e);
                  }
                  e.stopPropagation();
                }}
                style={{ float: 'right' }}>
                Remove Structure
              </ButtonWithIcon>
              <img
                className="search-structure-image"
                src={structure.structurePreview}
                width={structure.previewSize ? `${structure.previewSize.width}px` : undefined}
                alt="structure preview"
              />
              <ButtonWithIcon sprite="Edit" iconOnly type="link" className="edit-structure-icon" onClick={() => onStructureBuilderClick()}>
                Edit Structure
              </ButtonWithIcon>
            </div>
            <StructureOptionsBadges
              id={structure.structureUID}
              structureOptions={importedQueryValue ? structure.options : null}
              updateStructureOptionsCallback={updateOptions}
            />
          </>
        ))}
    </div>
  );
}

Structure.propTypes = {
  structureType: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    displayName: PropTypes.string,
    defaultOperation: PropTypes.string,
    canExist: PropTypes.bool,
    dataType: PropTypes.string,
    databases: PropTypes.arrayOf(PropTypes.string),
    databaseId: PropTypes.string,
    childrenCount: PropTypes.number,

    structureData: PropTypes.any,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        defaultBoundOperator: PropTypes.string.isRequired,
        canExist: PropTypes.bool.isRequired,
        dataType: PropTypes.string.isRequired,
        databases: PropTypes.arrayOf(PropTypes.string).isRequired,
        databaseId: PropTypes.string.isRequired,
        childrenCount: PropTypes.number.isRequired,
        minimized: PropTypes.bool.isRequired,
        value: PropTypes.string.isRequired,
        operation: PropTypes.string.isRequired,
      })
    ),
    minimized: PropTypes.bool,
    operator: PropTypes.string,
    exist: PropTypes.bool,
    uuid: PropTypes.string,
  }).isRequired,
};

export default Structure;
